import { motion } from "framer-motion";
import { ArrowRight, Clock, TrendingUp, Users, Check, Search, Filter } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import blog_background from "../../../Images/blogs/blog_background.jpg";
import blog_1 from "../../../Images/blogs/blog1.png";
import blog_2 from "../../../Images/blogs/blog2.png";
import blog_3 from "../../../Images/blogs/blog3.png";

const Blogs = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const blogs = [
    {
      id: 1,
      title: "The Secret to Hiring High-Performing Employees",
      excerpt:
        "Why qualifications alone aren't enough — and how behavioral science can transform your hiring process.",
      category: "Talent Acquisition",
      readTime: "5 min read",
      url: "/blogs/16052024",
      image: blog_1,
      datePublished: "2025-01-08",
      author: "SelfSync Team"
    },
    {
      id: 2,
      title:
        "You're Probably Hiring Wrong — Here's the Fix That's Backed by Science",
      excerpt:
        "Skills matter, but they don't guarantee success. Discover the science-backed solution to better hiring.",
      category: "Hiring Strategies",
      readTime: "4 min read",
      url: "/blogs/15052024",
      image: blog_2,
      datePublished: "2025-02-11",
      author: "SelfSync Team"
    },
    {
      id: 3,
      title: "One Simple Test That Can Save You From a Bad Hire",
      excerpt:
        "A bad hire isn't just disappointing — it's expensive. Learn how to avoid costly hiring mistakes.",
      category: "Workforce Optimization",
      readTime: "6 min read",
      url: "/blogs/12052024",
      image: blog_3,
      datePublished: "2025-03-12",
      author: "SelfSync Team"
    },
  ];

  const categories = ["All", "Talent Acquisition", "Hiring Strategies", "Workforce Optimization", "AI Technology"];

  // Filter blogs based on search and category
  const filteredBlogs = blogs.filter(blog => {
    const matchesSearch = blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === "All" || blog.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  // Schema markup for blog listing page
  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "SelfSync Blog",
    description: "Expert insights on hiring, talent assessment, and team optimization with SelfSync's AI-powered platform.",
    url: "https://selfsync.ai/blogs",
    publisher: {
      "@type": "Organization",
      name: "SelfSync",
      logo: {
        "@type": "ImageObject",
        url: "https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png"
      }
    },
    blogPost: blogs.map(blog => ({
      "@type": "BlogPosting",
      headline: blog.title,
      description: blog.excerpt,
      url: `https://selfsync.ai${blog.url}`,
      datePublished: blog.datePublished,
      author: {
        "@type": "Organization",
        name: "SelfSync"
      },
      publisher: {
        "@type": "Organization",
        name: "SelfSync",
        logo: {
          "@type": "ImageObject",
          url: "https://selfsync.ai/static/media/SelfSync%20Logo.df7570a5489657df44eb.png"
        }
      },
      image: {
        "@type": "ImageObject",
        url: `https://selfsync.ai${blog.image}`
      }
    }))
  };

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return (
    <div className="overflow-x-hidden">
      <Helmet>
        <title>SelfSync Blog | AI-Powered Talent Assessment & Hiring Insights</title>
        <meta
          name="description"
          content="Discover expert insights on hiring, talent assessment, and team optimization with SelfSync's AI-powered platform. Learn science-backed hiring strategies."
        />
        <meta name="keywords" content="talent assessment, hiring insights, AI recruitment, behavioral science, team optimization, hiring strategies" />
        <link rel="canonical" href="https://selfsync.ai/blogs" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="SelfSync Blog | AI-Powered Talent Assessment & Hiring Insights" />
        <meta property="og:description" content="Discover expert insights on hiring, talent assessment, and team optimization with SelfSync's AI-powered platform." />
        <meta property="og:url" content="https://selfsync.ai/blogs" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`https://selfsync.ai${blog_background}`} />
        
        {/* Schema markup */}
        <script type="application/ld+json">
          {JSON.stringify(blogListSchema)}
        </script>
      </Helmet>

      {/* Hero Section with Background Image */}
      <div className="relative h-96 md:h-[500px] w-full">
        {/* Background Image with Overlay */}
        <div className="absolute inset-0 bg-black/40 z-0">
          <img
            src={blog_background}
            alt="Team working together"
            className="w-full h-full object-cover opacity-60"
          />
          <div className="absolute inset-0 bg-black/65 opacity-40 mix-blend-multiply" />
        </div>

        {/* Content Overlay */}
        <div className="relative z-10 h-full flex flex-col justify-center px-6 max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-block px-4 py-2 bg-white/20 backdrop-blur-sm text-white rounded-full mb-6 text-sm font-medium">
              💡 Latest Insights
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-white mb-4 leading-tight">
              Expert Insights on
              <span className="block text-yellow-400">AI-Powered Hiring</span>
            </h1>
            <p className="text-lg md:text-xl text-white/90 max-w-3xl mx-auto mb-8">
              Discover proven strategies, behavioral science insights, and best practices 
              for building high-performing teams with 99% accuracy.
            </p>
            
            {/* Search Bar */}
            <div className="max-w-2xl mx-auto relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-12 pr-4 py-2 md:py-4 rounded-lg text-gray-900 text-lg border-0 focus:ring-4 focus:ring-white/20 shadow-lg"
              />
            </div>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-16">
        
        {/* Category Filter */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-12"
        >
         <div className="flex flex-wrap justify-center gap-2 sm:gap-3 px-2 sm:px-0">
  {categories.map((category) => (
    <button
      key={category}
      onClick={() => setSelectedCategory(category)}
      className={`px-4 sm:px-6 py-2 sm:py-3 rounded-full text-sm sm:text-base font-medium transition-all ${
        selectedCategory === category
          ? "bg-purple-600 text-white shadow-lg"
          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
      }`}
    >
      {category}
    </button>
  ))}
</div>

        </motion.div>

        {/* Search Results Info */}
        {(searchTerm || selectedCategory !== "All") && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="mb-8 text-center"
          >
            <p className="text-gray-600">
              {filteredBlogs.length === 0 
                ? "No articles found. Try different keywords or categories."
                : `Found ${filteredBlogs.length} article${filteredBlogs.length !== 1 ? 's' : ''} ${searchTerm ? `for "${searchTerm}"` : ''} ${selectedCategory !== "All" ? `in ${selectedCategory}` : ''}`
              }
            </p>
          </motion.div>
        )}

        {/* Featured Blogs */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="mb-16"
        >
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              {searchTerm || selectedCategory !== "All" ? "Search Results" : "Featured Articles"}
            </h2>
            <p className="text-gray-600 max-w-2xl mx-auto">
              Explore research-backed insights on talent assessment, team optimization, 
              and building winning organizations with AI-powered hiring.
            </p>
          </div>

          {/* Blog Cards Grid */}
          {filteredBlogs.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No articles found</h3>
              <p className="text-gray-500 mb-6">Try adjusting your search terms or browse all categories</p>
              <button
                onClick={() => {
                  setSearchTerm("");
                  setSelectedCategory("All");
                }}
                className="bg-purple-600 text-white px-6 py-3 rounded-lg hover:bg-purple-700 transition-colors"
              >
                Show All Articles
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredBlogs.map((blog, index) => (
                <motion.div
                  key={blog.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -5 }}
                  viewport={{ once: true }}
                  className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 cursor-pointer"
                  onClick={() => navigate(blog.url)}
                >{console.log("bbb",blog)}
                  {/* Blog Image */}
                  <div className="h-48 overflow-hidden">
                    <img
                      src={blog.image}
                      alt={blog.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                    />
                  </div>

                  {/* Blog Content */}
                  <div className="p-6">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-sm font-medium text-purple-600 bg-purple-50 px-3 py-1 rounded-full">
                        {blog.category}
                      </span>
                      <span className="text-sm text-gray-500 flex items-center">
                        <Clock className="w-4 h-4 mr-1" /> {blog.readTime}
                      </span>
                    </div>
                    
                    <h3 className="text-xl font-bold text-gray-900 mb-3 line-clamp-2 hover:text-purple-600 transition-colors">
                      {blog.title}
                    </h3>
                    
                    <p className="text-gray-600 mb-4 line-clamp-2">
                      {blog.excerpt}
                    </p>
                    
                    <div className="flex justify-between items-center pt-4 border-t border-gray-100">
                      <div className="text-sm text-gray-500">
                        {new Date(blog.datePublished).toLocaleDateString('en-US', {
                          month: 'long',
                          day: 'numeric',
                          year: 'numeric'
                        })}
                      </div>
                      <span className="text-purple-600 font-medium inline-flex items-center hover:text-purple-800 transition-colors">
                        Read more <ArrowRight className="w-4 h-4 ml-1" />
                      </span>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </motion.div>

        {/* Newsletter Signup */}
        {/* <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-purple-600 to-cyan-600 rounded-2xl p-8 md:p-12 mb-16 text-white text-center"
        >
          <h3 className="text-2xl md:text-3xl font-bold mb-4">
            Stay Ahead with AI Hiring Insights
          </h3>
          <p className="text-lg mb-8 max-w-2xl mx-auto opacity-90">
            Get the latest research, case studies, and best practices delivered to your inbox weekly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 max-w-lg mx-auto">
            <input
              type="email"
              placeholder="Enter your email"
              className="flex-1 px-4 py-3 rounded-lg text-gray-900 border-0 focus:ring-4 focus:ring-white/20"
            />
            <button className="bg-white text-purple-600 px-8 py-3 rounded-lg font-bold hover:bg-gray-100 transition-colors">
              Subscribe
            </button>
          </div>
          <p className="text-sm opacity-80 mt-4">
            Join 2,000+ HR leaders. Unsubscribe anytime.
          </p>
        </motion.div> */}

        {/* Benefits Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8 md:p-12 mb-16"
        >
          <div className="max-w-4xl mx-auto text-center">
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6">
              Why 260+ Companies Choose SelfSync for Talent Assessment
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-10">
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-purple-100 rounded-xl flex items-center justify-center mb-4">
                  <Users className="w-6 h-6 text-purple-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  99% Accuracy Rate
                </h4>
                <p className="text-gray-700 text-center">
                  AI-powered assessments predict job performance with unprecedented precision
                </p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4">
                  <TrendingUp className="w-6 h-6 text-blue-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  3x Better Retention
                </h4>
                <p className="text-gray-700 text-center">
                  Personality-matched hires stay longer and perform better in their roles
                </p>
              </div>
              <div className="flex flex-col items-center">
                <div className="w-14 h-14 bg-green-100 rounded-xl flex items-center justify-center mb-4">
                  <Check className="w-6 h-6 text-green-600" />
                </div>
                <h4 className="text-lg font-semibold text-gray-900 mb-2">
                  15 Minute Setup
                </h4>
                <p className="text-gray-700 text-center">
                  Start assessing candidates immediately with our intuitive platform
                </p>
              </div>
            </div>
          </div>
        </motion.div>


      </div>
    </div>
  );
};

export default Blogs;
